import React, { useState, useRef } from 'react';
import { IconButton, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';
import api from '../../Services/Api';

const VoiceInput = ({ value, onResult }) => {
    const { t } = useTranslation();
    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(false);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = async () => {
        try {
            setError(false);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = async () => {
                stream.getTracks().forEach(track => track.stop());
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setProcessing(true);
                try {
                    const result = await api.UploadAudio(blob);
                    const transcribed = result.text;
                    if (value && value.trim()) {
                        try {
                            const merged = await api.RewriteText(
                                value + "\n" + transcribed,
                                "Merge and improve the following text into a single coherent text. Keep all information."
                            );
                            onResult(merged.text);
                        } catch {
                            onResult(value + " " + transcribed);
                        }
                    } else {
                        onResult(transcribed);
                    }
                } catch {
                    setError(true);
                    setTimeout(() => setError(false), 2000);
                } finally {
                    setProcessing(false);
                }
            };

            mediaRecorder.start();
            setRecording(true);
        } catch {
            setError(true);
            setTimeout(() => setError(false), 2000);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        setRecording(false);
    };

    if (processing) {
        return (
            <IconButton size="small" disabled sx={{ minWidth: 44, minHeight: 44 }}>
                <CircularProgress size={20} sx={{ color: '#76A9AC' }} />
            </IconButton>
        );
    }

    return (
        <IconButton
            onClick={recording ? stopRecording : startRecording}
            aria-label={recording ? t('stopRecording') : t('voiceInput')}
            data-testid="voice-input"
            sx={{
                minWidth: 44,
                minHeight: 44,
                color: error ? '#ef5350' : recording ? '#ef5350' : 'rgba(255,255,255,0.7)',
                animation: recording ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                },
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                },
            }}
        >
            {recording ? <StopIcon /> : <MicIcon />}
        </IconButton>
    );
};

export default VoiceInput;
